import { useEffect, useState, useRef } from "react";
import { api, fileSrc } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Calendar, MapPin, Plus, Users, Trash2, Image as ImageIcon, X } from "lucide-react";
import { toast } from "sonner";

const EVENT_IMAGES = [
  "https://images.pexels.com/photos/27471164/pexels-photo-27471164.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  "https://images.pexels.com/photos/33261842/pexels-photo-33261842.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
];

export default function Events() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ title: "", description: "", location: "", starts_at: "" });
  const [eventImage, setEventImage] = useState(null);
  const [uploadingImg, setUploadingImg] = useState(false);
  const imgRef = useRef(null);

  const uploadImage = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setUploadingImg(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post("/files/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setEventImage(data);
    } catch {
      toast.error("Image upload failed");
    } finally {
      setUploadingImg(false);
      if (imgRef.current) imgRef.current.value = "";
    }
  };

  const load = async () => {
    const { data } = await api.get("/events");
    setItems(data);
  };
  useEffect(() => { load(); }, []);

  const canManage = (ev) => user?.role === "owner" || user?.role === "admin" || ev.author_id === user?.user_id;

  const removeEvent = async (ev, e) => {
    e.stopPropagation();
    if (!window.confirm(`Delete the event "${ev.title}"?`)) return;
    try {
      await api.delete(`/events/${ev.id}`);
      toast.success("Event deleted");
      load();
    } catch (er) {
      toast.error(er.response?.data?.detail || "Failed to delete");
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    try {
      const image_url = eventImage ? eventImage.id : EVENT_IMAGES[Math.floor(Math.random() * EVENT_IMAGES.length)];
      await api.post("/events", {
        ...form,
        starts_at: new Date(form.starts_at).toISOString(),
        image_url,
      });
      setForm({ title: "", description: "", location: "", starts_at: "" });
      setEventImage(null);
      setOpen(false);
      toast.success("Event created");
      load();
    } catch {
      toast.error("Failed");
    }
  };

  const toggleRsvp = async (ev) => {
    try {
      await api.post(`/events/${ev.id}/rsvp`);
      load();
    } catch {
      toast.error("Failed");
    }
  };

  return (
    <div className="space-y-6 animate-fade-up">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] text-purple-700 font-bold">Coordinate</p>
          <h1 className="text-4xl sm:text-5xl font-black font-display tracking-tight">Events</h1>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="new-event-btn" className="rounded-full bg-amber-500 hover:bg-amber-600 text-slate-900 font-bold btn-lift h-11 px-5">
              <Plus className="w-4 h-4 mr-1" /> New event
            </Button>
          </DialogTrigger>
          <DialogContent className="rounded-3xl">
            <DialogHeader><DialogTitle className="font-display text-2xl">Create event</DialogTitle></DialogHeader>
            <form onSubmit={submit} className="space-y-4">
              <div>
                <Label className="font-semibold">Title</Label>
                <Input data-testid="event-title-input" required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="mt-1 rounded-xl h-11" />
              </div>
              <div>
                <Label className="font-semibold">Description</Label>
                <Textarea data-testid="event-description-input" required value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={3} className="mt-1 rounded-xl" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="font-semibold">Location</Label>
                  <Input data-testid="event-location-input" required value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} className="mt-1 rounded-xl h-11" />
                </div>
                <div>
                  <Label className="font-semibold">Date & time</Label>
                  <Input data-testid="event-date-input" type="datetime-local" required value={form.starts_at} onChange={(e) => setForm({ ...form, starts_at: e.target.value })} className="mt-1 rounded-xl h-11" />
                </div>
              </div>
              <div>
                <Label className="font-semibold">Event photo</Label>
                <input ref={imgRef} type="file" accept="image/*" className="hidden" onChange={uploadImage} data-testid="event-image-input" />
                {eventImage ? (
                  <div className="mt-1 relative rounded-xl overflow-hidden border border-slate-200">
                    <img src={fileSrc(eventImage.id)} alt="preview" className="w-full h-40 object-cover" />
                    <button type="button" onClick={() => setEventImage(null)} data-testid="event-image-remove" className="absolute top-2 right-2 w-8 h-8 rounded-full bg-white/90 text-red-500 flex items-center justify-center shadow">
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ) : (
                  <button type="button" onClick={() => imgRef.current?.click()} disabled={uploadingImg} data-testid="event-image-btn"
                    className="mt-1 w-full flex items-center justify-center gap-2 h-24 rounded-xl border-2 border-dashed border-slate-200 text-slate-500 hover:border-purple-300 hover:text-purple-700 transition-colors">
                    <ImageIcon className="w-5 h-5" /> {uploadingImg ? "Uploading…" : "Add a photo (optional)"}
                  </button>
                )}
              </div>
              <Button type="submit" data-testid="event-submit-btn" className="w-full rounded-2xl bg-purple-700 hover:bg-purple-800 h-11 font-bold">Create event</Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {items.length === 0 ? (
        <div className="text-center py-20 bg-white rounded-3xl border border-dashed border-slate-200">
          <Calendar className="w-12 h-12 text-purple-300 mx-auto" />
          <p className="mt-4 text-lg font-bold text-slate-700">No events scheduled</p>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {items.map((ev) => (
            <article key={ev.id} data-testid={`event-card-${ev.id}`}
              className="group bg-white rounded-3xl overflow-hidden border border-slate-100 shadow-sm hover:shadow-lg btn-lift">
              <div className="aspect-[16/10] overflow-hidden relative">
                <img src={ev.image_url?.startsWith("http") ? ev.image_url : fileSrc(ev.image_url)} alt={ev.title}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                {canManage(ev) && (
                  <button
                    onClick={(e) => removeEvent(ev, e)}
                    data-testid={`delete-event-${ev.id}`}
                    className="absolute top-3 right-3 w-9 h-9 rounded-full bg-white/90 hover:bg-white text-red-500 flex items-center justify-center shadow-md"
                    title="Delete event"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                )}
              </div>
              <div className="p-5 space-y-3">
                <h3 className="text-xl font-black font-display text-slate-900">{ev.title}</h3>
                <div className="flex flex-col gap-1 text-sm text-slate-600">
                  <div className="flex items-center gap-2"><Calendar className="w-4 h-4 text-purple-700" /> {new Date(ev.starts_at).toLocaleString()}</div>
                  <div className="flex items-center gap-2"><MapPin className="w-4 h-4 text-coral-500" style={{ color: "#FF5757" }} /> {ev.location}</div>
                  <div className="flex items-center gap-2"><Users className="w-4 h-4 text-emerald-500" /> {ev.rsvp_count} going</div>
                </div>
                <p className="text-sm text-slate-600 line-clamp-2">{ev.description}</p>
                <Button
                  onClick={() => toggleRsvp(ev)}
                  data-testid={`rsvp-btn-${ev.id}`}
                  className={`w-full rounded-2xl h-11 font-bold ${
                    ev.user_rsvp
                      ? "bg-emerald-500 hover:bg-emerald-600 text-white"
                      : "bg-purple-700 hover:bg-purple-800 text-white"
                  }`}
                >
                  {ev.user_rsvp ? "You're going ✓" : "RSVP"}
                </Button>
              </div>
            </article>
          ))}
        </div>
      )}
    </div>
  );
}
